import { ref, computed, watch } from 'vue'

export function usePriceHistogramSlider(props, emit) {
  // Generate complete bars array including missing ranges with count 0
  const completeBars = computed(() => {
    const allBars = []
    const existingBarsMap = new Map()
    
    // Create map of existing bars by rangeFrom for quick lookup
    props.bars.forEach(bar => {
      existingBarsMap.set(bar.rangeFrom, bar)
    })
    
    // Generate all possible ranges from min to max with step
    for (let rangeFrom = props.min; rangeFrom < props.max; rangeFrom += props.step) {
      const rangeTo = rangeFrom + props.step - 1
      
      if (existingBarsMap.has(rangeFrom)) {
        // Use existing bar data
        allBars.push(existingBarsMap.get(rangeFrom))
      } else {
        // Create missing bar with count 0
        allBars.push({
          rangeFrom: rangeFrom,
          rangeTo: rangeTo,
          count: 0
        })
      }
    }
    
    return allBars
  })

  // --- 1) Inicializace `local` jednou z `modelValue`
  const local = ref([
    // pokud rodič neposílá nic, fallback na celý rozsah
    props.modelValue[0] != null ? props.modelValue[0] : props.min,
    props.modelValue[1] != null ? props.modelValue[1] : props.max,
  ])

  // --- 2) Synchronizace kdykoliv rodič aktualizuje `modelValue`
  watch(
      () => props.modelValue,
      (nv) => {
        // když modelValue přijde z rodiče, nastavím rukojeti
        local.value = [
          nv[0] != null ? nv[0] : props.min,
          nv[1] != null ? nv[1] : props.max,
        ]
      },
      { immediate: true }
  )

  // Keep current selection when min/max change; only clamp into new bounds
  watch(
      [() => props.min, () => props.max],
      ([newMin, newMax]) => {
        let [from, to] = local.value

        // Fallback if nothing selected yet
        if (from == null) from = newMin
        if (to == null)   to   = newMax

        // Clamp into new bounds
        from = Math.max(newMin, Math.min(from, newMax))
        to   = Math.max(newMin, Math.min(to,   newMax))

        // Ensure at least one step gap between handles
        if (to < from + props.step) {
          to = Math.min(newMax, from + props.step)
          if (to < from + props.step) {
            // If even that fails (very small range), collapse to bounds
            from = newMin
            to   = Math.min(newMax, newMin + props.step)
          }
        }

        // Update only if changed
        if (from !== local.value[0] || to !== local.value[1]) {
          local.value = [from, to]
        }
      },
      { immediate: true }
  )

  // --- 3) Swap handles, pokud se překříží
  /**
   * @param {'lower'|'upper'} handle
   */
  function updateRange(handle) {
    if (handle === 'lower') {
      // never let lower cross above upper-step
      if (local.value[0] > local.value[1] - props.step) {
        local.value[0] = local.value[1] - props.step
      }
    } else {
      // never let upper cross below lower+step
      if (local.value[1] < local.value[0] + props.step) {
        local.value[1] = local.value[0] + props.step
      }
    }
  }

  // --- 4) Emit až na `change` (když uživatel pustí myš)
  function onChange() {
    updateRange()
    emit('update:modelValue', [ ...local.value ])
  }

  // --- 5) Scale pro bary
  const BAR_AREA_HEIGHT = 100
  const scale = computed(() => {
    if (!completeBars.value.length) return 1
    const maxCount = Math.max(...completeBars.value.map(b => b.count))
    return maxCount > 0 ? BAR_AREA_HEIGHT / maxCount : 1
  })

  // --- 6) Styl každého bar
  function barStyle(bar, selectedColor = '#3b5998', defaultColor = '#e0e0e0') {
    const total = props.max - props.min
    const inside = bar.rangeFrom >= local.value[0] && bar.rangeTo <= local.value[1]
    
    // Calculate height with minimum height for visibility
    const calculatedHeight = bar.count * scale.value
    const MIN_HEIGHT = 2 // minimum 2px height for visibility
    const height = bar.count === 0 ? MIN_HEIGHT : Math.max(calculatedHeight, MIN_HEIGHT)
    
    // Color logic: 0 count = always grey, 1+ count = depends on selection
    let backgroundColor
    if (bar.count === 0) {
      backgroundColor = defaultColor // Always grey for 0 count
    } else {
      backgroundColor = inside ? selectedColor : defaultColor
    }
    
    return {
      height:          `${height}px`,
      left:            `${(bar.rangeFrom - props.min)/total*100}%`,
      width:           `calc(${props.step/total*100}% - 1px)`,
      backgroundColor: backgroundColor,
    }
  }

  // --- 7) Styl vybraného úseku tracku
  const selectedTrackStyle = computed(() => {
    const [l,h] = local.value
    const total = props.max - props.min
    return {
      left:  `${(l   - props.min)/total*100}%`,
      width: `${(h-l )/total*100}%`,
    }
  })

  // --- 8) Formát CZK
  function formatMoney(v) {
    return new Intl.NumberFormat('cs-CZ', {
      style: 'currency', currency: 'CZK', maximumFractionDigits: 0
    }).format(v)
  }

  return {
    local,
    updateRange,
    onChange,
    scale,
    barStyle,
    selectedTrackStyle,
    formatMoney,
    completeBars
  }
}
